import PaiDotLoader from 'features/ui/base-components/pai-dot-loader';
import i18n from 'i18n';
import _ from 'lodash';
import React, { useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router';
import useErrorStore from 'stores/error-store';
import { makeStyles } from 'tss-react/mui';
import { getAnonymousLeadTagDetails, getQuestionnaire, sendAnonymousAnswerLeadQuestionnaire, sendAnswerLeadQuestionnaire } from '../API/lead';
import {
  Answer,
  Demographics,
  DemographicsPayload,
  Perspectives,
  QuestionnaireResponse,
} from '../features/lead/lead-questionnaire/lead-questionnaire.types';
import QuestionnaireComplete from '../features/lead/lead-questionnaire/questionnaire-complete';
import QuestionnaireError from '../features/lead/lead-questionnaire/questionnaire-error';
import QuestionnaireIntroduction from '../features/lead/lead-questionnaire/questionnaire-introduction';
import QuestionnairePerspective from '../features/lead/lead-questionnaire/questionnaire-perspective';
import QuestionnaireWelcome from '../features/lead/lead-questionnaire/questionnaire-welcome';
import Questions from '../features/lead/lead-questionnaire/questions';

const LeadQuestionnaire: React.FC = () => {
  const { classes, cx } = useStyles();
  const handleOpenErrorDialog = useErrorStore((state) => state.handleOpenErrorDialog);

  const location = useLocation();
  const contentRef = useRef<any>(null);
  const isFirstRender = useRef<boolean>(true);

  const query = new URLSearchParams(location.search);
  const leadTagId = query.get('tagId');
  const participantId = query.get('participantId');
  const tenantId = query.get('tenantId');

  const [participantLimitReached, setParticipantLimitReached] = useState(false);

  const [isAnonymous, setIsAnonymous] = useState<boolean>(false);
  const [anonymousPerspective, setAnonymousPerspective] = useState<Perspectives>(Perspectives.BOSS);

  const lsLang = localStorage.getItem('i18nextLng');
  const [language, setLanguage] = useState(lsLang ?? 'de');

  const [loading, setLoading] = useState(false);
  const [questionnaireResponse, setQuestionnaireResponse] = useState<QuestionnaireResponse>();
  const [closedAnswers, setClosedAnswers] = useState<Answer[]>([]);
  const [categoryIndex, setCategoryIndex] = useState(0);
  const [effectAnswers, setEffectAnswers] = useState<Answer[]>([]);
  const [openAnswers, setOpenAnswers] = useState<Answer[]>([]);
  const [demographicAnswers, setDemographicAnswers] = useState<Demographics>({
    branch: '',
    companySize: '',
    teamSize: '',
    managementField: '',
    teamType: '',
    customerResponsibility: '',
    responsibilityField: '',
  });

  const [showGreeting, setShowGreeting] = useState(true);
  const [showIntroduction, setShowIntroduction] = useState(false);
  const [showQuestionnaire, setShowQuestionnaire] = useState(false);
  const [showLastPage, setShowLastPage] = useState(false);

  const [sendAnswersLoading, setSendAnswersLoading] = useState(false);

  useEffect(() => {
    if (!_.isNil(leadTagId) && !_.isNil(tenantId)) {
      if (!_.isNil(participantId)) {
        setIsAnonymous(false);

        (async () => {
          if (isFirstRender.current) {
            setLoading(true);
          }
          const res = await getQuestionnaire(leadTagId, participantId, tenantId, language);
          if (res && !res.error) {
            setQuestionnaireResponse(res);
          }
          if (isFirstRender.current) {
            setLoading(false);
            isFirstRender.current = false;
          }
        })();
      }

      if (_.isNil(participantId)) {
        setIsAnonymous(true);
        setShowGreeting(false);

        (async () => {
          if (isFirstRender.current) {
            setLoading(true);
          }
          const res = await getAnonymousLeadTagDetails(leadTagId, tenantId, language);
          if (res && !res.error) {
            const questionnaire: QuestionnaireResponse = {
              name: res.name,
              closedQuestions: res.closedQuestions,
              openQuestions: res.openQuestions,
              effectQuestions: res.effectQuestions,
            };
            setQuestionnaireResponse(questionnaire);
          } else {
            if (res.error.code === 422) {
              setParticipantLimitReached(true);
            }
          }
          if (isFirstRender.current) {
            setLoading(false);
            isFirstRender.current = false;
          }
        })();
      }
    }
  }, [language]);

  const handleAnonymousPerspectiveChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setAnonymousPerspective(event.target.value as Perspectives);
  };

  const handleQuestionnaireLangChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setLanguage(event.target.value);
    localStorage.setItem('i18nextLng', event.target.value);
    i18n.changeLanguage(event.target.value);
  };

  const handleShowGreetingClick = () => {
    setShowGreeting(true);
  };

  const handleShowIntroductionClick = () => {
    setShowGreeting(false);
    setShowIntroduction(true);
  };

  const handleStartQuestionnaireClick = () => {
    setShowIntroduction(false);
    setShowQuestionnaire(true);
  };

  const scrollToTop = () => {
    contentRef?.current.scrollTo({
      top: 0,
      left: 0,
      behavior: 'smooth',
    });
  };

  const nextCategory = () => {
    setCategoryIndex(categoryIndex + 1);

    setTimeout(() => {
      scrollToTop();
    }, 225);
  };

  const previousCategory = () => {
    if (categoryIndex > 0) {
      setCategoryIndex(categoryIndex - 1);
      setTimeout(() => {
        scrollToTop();
      }, 225);
    }
  };

  /**
   * Answers handlers
   */

  // closes answers
  const handleClosedRadioSelectionChange = (id: string) => (value: string | number) => {
    return () => {
      const updateSelection = [...closedAnswers];
      const foundQuestion = updateSelection.find((question) => question.id === id);
      if (foundQuestion) {
        foundQuestion.value = +value;
      } else {
        updateSelection.push({ id, value: +value });
      }
      setClosedAnswers(updateSelection);
    };
  };

  // effect answers
  const handleEffectRadioSelectionChange = (id: string) => (value: string | number) => {
    return () => {
      const updateSelection = [...effectAnswers];
      const foundQuestion = updateSelection.find((question) => question.id === id);
      if (foundQuestion) {
        foundQuestion.value = +value;
      } else {
        updateSelection.push({ id, value: +value });
      }
      setEffectAnswers(updateSelection);
    };
  };

  // open answers
  const handleOpenTextFieldChange = (id: string) => {
    return (e: React.ChangeEvent<HTMLInputElement>) => {
      const updateSelection = [...openAnswers];
      const foundQuestion = updateSelection.find((question) => question.id === id);
      if (foundQuestion) {
        foundQuestion.value = e.target.value;
      } else {
        updateSelection.push({ id, value: e.target.value });
      }
      setOpenAnswers(updateSelection);
    };
  };

  // demographic answers
  const handleBranchCheckboxChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setDemographicAnswers((prev) => ({ ...prev, branch: event.target.value }));
  };

  const handleCompanySizeRadioChange = (value: string) => {
    return () => {
      setDemographicAnswers((prev) => ({ ...prev, companySize: value }));
    };
  };

  const handleTeamSizeRadioChange = (value: string) => {
    return () => {
      setDemographicAnswers((prev) => ({ ...prev, teamSize: value }));
    };
  };

  const handleManagementFieldRadioChange = (value: string) => {
    return () => {
      setDemographicAnswers((prev) => ({ ...prev, managementField: value }));
    };
  };

  const handleTeamTypeRadioChange = (value: string) => {
    return () => {
      setDemographicAnswers((prev) => ({ ...prev, teamType: value }));
    };
  };

  const handleCustomerResponsibilityRadioChange = (value: string) => {
    return () => {
      setDemographicAnswers((prev) => ({ ...prev, customerResponsibility: value }));
    };
  };

  const handleResponsibilityFieldRadioChange = (value: string) => {
    return () => {
      setDemographicAnswers((prev) => ({ ...prev, responsibilityField: value }));
    };
  };

  const sendAnswers = async () => {
    const closedAnswersPayload = closedAnswers.map((answer) => ({ question: answer.id, answer: +answer.value }));
    const effectAnswersPayload = effectAnswers.map((answer) => ({ question: answer.id, answer: +answer.value }));
    const openAnswersPayload = openAnswers.map((answer) => ({ question: answer.id, answer: [`${answer.value}`] }));
    const tempDemographicAnswers = { ...demographicAnswers };
    const demographicPayload: DemographicsPayload = {
      ...tempDemographicAnswers,
      customerResponsibility: tempDemographicAnswers.customerResponsibility === 'Ja',
    };

    if (isAnonymous && anonymousPerspective && leadTagId && tenantId) {
      setSendAnswersLoading(true);
      const res = await sendAnonymousAnswerLeadQuestionnaire(
        leadTagId,
        anonymousPerspective,
        tenantId,
        closedAnswersPayload,
        effectAnswersPayload,
        openAnswersPayload,
        demographicPayload
      );

      if (!res.error) {
        setShowQuestionnaire(false);
        setShowLastPage(true);
      } else {
        handleOpenErrorDialog(res?.error, '/lead/tags/:tagId/participant-completed?tenantId=tenantId', 'post');
      }
      setSendAnswersLoading(false);
    } else if (leadTagId && participantId && tenantId) {
      setSendAnswersLoading(true);
      const res = await sendAnswerLeadQuestionnaire(
        leadTagId,
        participantId,
        tenantId,
        closedAnswersPayload,
        effectAnswersPayload,
        openAnswersPayload,
        demographicPayload
      );

      if (!res.error) {
        setShowQuestionnaire(false);
        setShowLastPage(true);
      } else {
        handleOpenErrorDialog(res?.error, '/lead/tags/:tagId/participant/:participantId?tenantId=tenantId', 'post');
      }
      setSendAnswersLoading(false);
    }
  };

  const renderPage = () => {
    if (!questionnaireResponse || (!isAnonymous && questionnaireResponse?.completed) || (isAnonymous && participantLimitReached)) {
      return (
        <QuestionnaireError completed={questionnaireResponse && questionnaireResponse.completed} participantLimitReached={participantLimitReached} />
      );
    } else if (isAnonymous && !showGreeting && !showIntroduction && !showQuestionnaire && !showLastPage) {
      return (
        <QuestionnairePerspective
          perspective={anonymousPerspective}
          handleAnonymousPerspectiveChange={handleAnonymousPerspectiveChange}
          handleShowGreetingClick={handleShowGreetingClick}
        />
      );
    } else if (showGreeting && questionnaireResponse) {
      return (
        <QuestionnaireWelcome
          perspective={!isAnonymous ? questionnaireResponse.perspective : anonymousPerspective}
          handleShowIntroductionClick={handleShowIntroductionClick}
        />
      );
    } else if (showIntroduction && questionnaireResponse) {
      return (
        <QuestionnaireIntroduction
          perspective={!isAnonymous ? questionnaireResponse.perspective : anonymousPerspective}
          language={language}
          handleQuestionnaireLangChange={handleQuestionnaireLangChange}
          handleStartQuestionnaireClick={handleStartQuestionnaireClick}
        />
      );
    } else if (showQuestionnaire && questionnaireResponse) {
      return (
        <Questions
          categoryIndex={categoryIndex}
          perspective={!isAnonymous ? questionnaireResponse.perspective : anonymousPerspective}
          closedQuestions={questionnaireResponse.closedQuestions}
          openQuestions={questionnaireResponse.openQuestions}
          effectQuestions={questionnaireResponse.effectQuestions}
          closedAnswers={closedAnswers}
          effectAnswers={effectAnswers}
          openAnswers={openAnswers}
          demographicAnswers={demographicAnswers}
          handleClosedRadioSelectionChange={handleClosedRadioSelectionChange}
          handleEffectRadioSelectionChange={handleEffectRadioSelectionChange}
          handleOpenTextFieldChange={handleOpenTextFieldChange}
          nextCategory={nextCategory}
          previousCategory={previousCategory}
          scrollToTop={scrollToTop}
          sendAnswers={sendAnswers}
          sendAnswersLoading={sendAnswersLoading}
          handleBranchCheckboxChange={handleBranchCheckboxChange}
          handleCompanySizeRadioChange={handleCompanySizeRadioChange}
          handleTeamSizeRadioChange={handleTeamSizeRadioChange}
          handleManagementFieldRadioChange={handleManagementFieldRadioChange}
          handleTeamTypeRadioChange={handleTeamTypeRadioChange}
          handleCustomerResponsibilityRadioChange={handleCustomerResponsibilityRadioChange}
          handleResponsibilityFieldRadioChange={handleResponsibilityFieldRadioChange}
        />
      );
    } else if (showLastPage) {
      return <QuestionnaireComplete />;
    } else {
      return <QuestionnaireError />;
    }
  };

  return (
    <div ref={contentRef} className={cx(classes.content, loading ? 'center' : undefined)}>
      {loading ? <PaiDotLoader size={'large'} /> : renderPage()}
    </div>
  );
};

const useStyles = makeStyles()(() => ({
  content: {
    padding: '24px',
    height: '100dvh',
    overflowX: 'hidden',
    '&.center': {
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
    },
  },
}));

export default LeadQuestionnaire;
